import { Service } from '@angular/core';
import type {
  OnyxAddress,
  OnyxAddressByType,
  OnyxAddressService,
  OnyxPaginated,
} from '@onyx/angular';
import {
  AddressHelper,
  OnyxAddressType,
  OnyxRouteLegType,
} from '@onyx/angular';
import { chain } from 'lodash';
import type { Observable } from 'rxjs';
import { map, of } from 'rxjs';
import { AddressStorageKey } from '../enums/storage/address-storage-key';
import { TransitHelper } from '../helpers/transit.helper';
import type { CalculateRouteRequest } from '../interfaces/address/calculate-route-request';
import type { CalculateRouteResponse } from '../interfaces/address/calculate-route-response';
import { ApiService } from './api.service';

@Service()
export class AddressService extends ApiService implements OnyxAddressService {
  private recentSearches: OnyxAddress[] = [];

  constructor() {
    super();

    const recentSearches = localStorage.getItem(
      AddressStorageKey.RECENT_SEARCHES,
    );
    this.recentSearches = JSON.parse(recentSearches ?? '[]');
  }

  public geocode<T extends OnyxAddressType>(
    types: T[],
    query: string,
    limit: number,
  ): Observable<OnyxPaginated<OnyxAddressByType[T]>> {
    return this.get('/geocode', {
      params: {
        'types[]': types,
        query,
        limit,
      },
    });
  }

  public getRecentSearches<T extends OnyxAddressType>(
    types: T[],
    limit: number,
  ): Observable<OnyxAddressByType[T][]> {
    return chain(this.recentSearches)
      .filter((address): address is OnyxAddressByType[T] =>
        types.includes(address.type as T),
      )
      .thru((addresses) => of(addresses.slice(0, limit)))
      .value();
  }

  public addToRecentSearches(address: OnyxAddress): Observable<void> {
    if (address.type !== OnyxAddressType.CUSTOM) return of(undefined);

    this.recentSearches = chain(this.recentSearches)
      .unshift(address)
      .uniqBy((address) => AddressHelper.composeCoordinatesLabel(address))
      .value();

    localStorage.setItem(
      AddressStorageKey.RECENT_SEARCHES,
      JSON.stringify(this.recentSearches),
    );

    return of(undefined);
  }

  public calculateRoute(
    body: CalculateRouteRequest,
  ): Observable<CalculateRouteResponse> {
    return this.post<CalculateRouteResponse>('/calculate-route', body).pipe(
      map((response) => {
        for (const route of response.routes) {
          for (const point of route.points) {
            let departure = point.departure;

            for (const leg of point.legs) {
              if (leg.type === OnyxRouteLegType.VEHICLE) {
                departure = leg.arrival;
                continue;
              }

              const transitType = TransitHelper.fromCoordinates(
                departure,
                leg.arrival,
              )!.type;

              leg.type = transitType as unknown as OnyxRouteLegType;
              departure = leg.arrival;
            }
          }
        }

        return response;
      }),
    );
  }
}
